<script setup lang="ts">
import { computed } from 'vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import {
  FileText,
  Users,
  Activity,
  Clock,
  Hash,
  Calendar,
  DollarSign,
  CheckCircle2,
  XCircle,
  Eye
} from 'lucide-vue-next'

interface TariffCodeItem {
  rate_id: string
  tariff_code: {
    id: number
    code: string
    code_description: string
    is_hbc: boolean
    benefit_type: Array<{
      id: number
      code: string
      code_description: string
    }>
    date_created: string
    date_modified: string
  }
  practice_type: Array<{
    code: string
    code_description: string
  }>
  billing_type: {
    code: string
    code_description: string
  }
  limit_type: {
    id: number
    code: string
    code_description: string
  }
  frequency: {
    id: number
    code: string
    code_description: string
  }
  applicable_year: number
  applicable_limits: number
  active: boolean
  date_created: string
  date_modified: string
}

interface Props {
  tariff: TariffCodeItem
  open?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

// Format date
const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Get status color
const statusColor = computed(() => props.tariff.active ? 'text-green-600' : 'text-gray-500')
const statusBg = computed(() => props.tariff.active ? 'bg-green-50' : 'bg-gray-50')
const statusBorder = computed(() => props.tariff.active ? 'border-green-200' : 'border-gray-200')
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="bg-slate-100 w-[70vw] max-w-none sm:max-w-none overflow-y-auto">
      <DialogHeader>
        <div class="mt-6 flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-2">
              <FileText class="h-6 w-6 text-blue-600" />
              <DialogTitle class="text-2xl">{{ tariff.tariff_code.code }}</DialogTitle>
              <Badge v-if="tariff.tariff_code.is_hbc" variant="default" class="ml-2">HBC</Badge>
            </div>
            <DialogDescription class="text-base">
              {{ tariff.tariff_code.code_description }}
            </DialogDescription>
          </div>
          
          <div class="flex flex-col items-end gap-2">
            <div 
              :class="[
                'flex items-center gap-2 px-3 py-1 rounded-full border',
                statusBg,
                statusBorder
              ]"
            >
              <div :class="['w-2 h-2 rounded-full', tariff.active ? 'bg-green-500' : 'bg-gray-400']"></div>
              <span :class="['text-sm font-medium', statusColor]">
                {{ tariff.active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <Badge variant="secondary" class="text-lg font-bold">
              {{ tariff.applicable_year }}
            </Badge>
          </div>
        </div>
      </DialogHeader>
      
      <ScrollArea class="h-[calc(90vh-200px)] pr-4">
        <div class="space-y-6 pt-4">
          <!-- Benefit Types -->
          <div v-if="tariff.tariff_code.benefit_type?.length" class="space-y-3">
            <div class="flex items-center gap-2">
              <Activity class="h-5 w-5 text-purple-600" />
              <h3 class="text-sm font-semibold text-gray-700">
                Benefit Types ({{ tariff.tariff_code.benefit_type.length }})
              </h3>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div 
                v-for="benefit in tariff.tariff_code.benefit_type" 
                :key="benefit.id"
                class="bg-purple-50 border border-purple-200 rounded-lg p-3"
              >
                <div class="flex items-center gap-2">
                  <Badge variant="outline" class="font-mono bg-white">{{ benefit.code }}</Badge>
                  <span class="text-sm text-purple-900 font-medium">{{ benefit.code_description }}</span>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          <!-- Key Metrics Grid -->
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <!-- Billing Type -->
            <div class="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <DollarSign class="h-4 w-4 text-cyan-600" />
                <p class="text-xs font-semibold text-cyan-700">Billing Type</p>
              </div>
              <p class="text-lg font-bold text-cyan-900">{{ tariff.billing_type.code }}</p>
              <p class="text-xs text-cyan-600 mt-1">{{ tariff.billing_type.code_description }}</p>
            </div>

            <!-- Limit Type -->
            <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <Activity class="h-4 w-4 text-orange-600" />
                <p class="text-xs font-semibold text-orange-700">Limit Type</p>
              </div>
              <p class="text-lg font-bold text-orange-900">{{ tariff.limit_type.code }}</p>
              <p class="text-xs text-orange-600 mt-1">{{ tariff.limit_type.code_description }}</p>
            </div>

            <!-- Frequency -->
            <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <Clock class="h-4 w-4 text-indigo-600" />
                <p class="text-xs font-semibold text-indigo-700">Frequency</p>
              </div>
              <p class="text-lg font-bold text-indigo-900">{{ tariff.frequency.code }}</p>
              <p class="text-xs text-indigo-600 mt-1">{{ tariff.frequency.code_description }}</p>
            </div>

            <!-- Applicable Limits -->
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <Hash class="h-4 w-4 text-green-600" />
                <p class="text-xs font-semibold text-green-700">Applicable Limits</p>
              </div>
              <p class="text-3xl font-bold text-green-900">{{ tariff.applicable_limits }}</p>
            </div>

            <!-- Tariff Code ID -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <Hash class="h-4 w-4 text-blue-600" />
                <p class="text-xs font-semibold text-blue-700">Tariff Code ID</p>
              </div>
              <p class="text-3xl font-bold text-blue-900">{{ tariff.tariff_code.id }}</p>
            </div>

            <!-- Limit Type ID -->
            <div class="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <Hash class="h-4 w-4 text-pink-600" />
                <p class="text-xs font-semibold text-pink-700">Limit Type ID</p>
              </div>
              <p class="text-3xl font-bold text-pink-900">{{ tariff.limit_type.id }}</p>
            </div>
          </div>

          <Separator />

          <!-- Practice Types Accordion -->
          <div v-if="tariff.practice_type?.length" class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <Users class="h-5 w-5 text-gray-600" />
                <h3 class="text-sm font-semibold text-gray-700">
                  Practice Types
                </h3>
              </div>
              <Badge variant="secondary">{{ tariff.practice_type.length }} types</Badge>
            </div>

            <Accordion type="single" collapsible class="w-full">
              <AccordionItem value="practice-types">
                <AccordionTrigger class="hover:no-underline">
                  <span class="text-sm">View all {{ tariff.practice_type.length }} practice types</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2 pt-4">
                    <div 
                      v-for="practice in tariff.practice_type" 
                      :key="practice.code"
                      class="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <Badge variant="outline" class="font-mono flex-shrink-0">{{ practice.code }}</Badge>
                      <span class="text-sm text-gray-700">{{ practice.code_description }}</span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <Separator />

          <!-- Metadata Section -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div class="flex items-center gap-2 mb-4">
              <Calendar class="h-5 w-5 text-gray-600" />
              <h3 class="text-sm font-semibold text-gray-700">Metadata & Timestamps</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-3">
                <div>
                  <p class="text-xs text-gray-500 mb-1">Rate ID</p>
                  <p class="text-sm font-mono text-gray-700 break-all bg-white p-2 rounded border border-gray-200">
                    {{ tariff.rate_id }}
                  </p>
                </div>
                
                <div>
                  <p class="text-xs text-gray-500 mb-1">Rate Created</p>
                  <p class="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                    {{ formatDate(tariff.date_created) }}
                  </p>
                </div>
                
                <div>
                  <p class="text-xs text-gray-500 mb-1">Rate Modified</p>
                  <p class="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                    {{ formatDate(tariff.date_modified) }}
                  </p>
                </div>
              </div>
              
              <div class="space-y-3">
                <div>
                  <p class="text-xs text-gray-500 mb-1">Tariff Code Created</p>
                  <p class="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                    {{ formatDate(tariff.tariff_code.date_created) }}
                  </p>
                </div>
                
                <div>
                  <p class="text-xs text-gray-500 mb-1">Tariff Code Modified</p>
                  <p class="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                    {{ formatDate(tariff.tariff_code.date_modified) }}
                  </p>
                </div>

                <div>
                  <p class="text-xs text-gray-500 mb-1">Frequency ID</p>
                  <p class="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                    {{ tariff.frequency.id }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Status Summary -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 class="text-sm font-semibold text-blue-900 mb-3">Summary</h3>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex items-center gap-2">
                <CheckCircle2 v-if="tariff.active" class="h-4 w-4 text-green-600" />
                <XCircle v-else class="h-4 w-4 text-gray-400" />
                <span class="text-gray-700">
                  Status: <strong>{{ tariff.active ? 'Active' : 'Inactive' }}</strong>
                </span>
              </div>
              <div class="flex items-center gap-2">
                <CheckCircle2 v-if="tariff.tariff_code.is_hbc" class="h-4 w-4 text-blue-600" />
                <XCircle v-else class="h-4 w-4 text-gray-400" />
                <span class="text-gray-700">
                  HBC: <strong>{{ tariff.tariff_code.is_hbc ? 'Yes' : 'No' }}</strong>
                </span>
              </div>
              <div class="flex items-center gap-2">
                <Activity class="h-4 w-4 text-purple-600" />
                <span class="text-gray-700">
                  <strong>{{ tariff.tariff_code.benefit_type.length }}</strong>
                  Benefit Types
                </span>
              </div>
              <div class="flex items-center gap-2">
                <Users class="h-4 w-4 text-gray-600" />
                <span class="text-gray-700">
                  <strong>{{ tariff.practice_type.length }}</strong>
                  Practice Types
                </span>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
</template>